import { finance, financeKeys } from "@/services/finance";
import { Alert, Button, Skeleton } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import classes from "@/modules/finance/finance.module.css";
import { faDate, faNum, rial, toNumber, useFinance } from "@/modules/finance/shared";

// Sales, receipts, payments and receivables are four different numbers; none of them is profit.
export default function Overview() {
  const { range, can } = useFinance();
  const overview = useQuery({ queryKey: [...financeKeys.all, "overview", range], queryFn: () => finance.overview(range.from, range.to) });
  const unpaid = useQuery({ queryKey: [...financeKeys.all, "expenses", "follow-up"], queryFn: () => finance.expenses({ limit: 100 }) });
  const products = useQuery({ queryKey: [...financeKeys.all, "products"], queryFn: () => finance.products(), enabled: can("finance.view") });
  const day = useQuery({ queryKey: [...financeKeys.all, "daily", range.to], queryFn: () => finance.dailyPreview(range.to), enabled: can("finance.close") });

  if (overview.isLoading) return <><Skeleton height={100} radius={14} /><Skeleton height={220} radius={14} /></>;
  if (overview.isError) return <Alert color="red">دریافت نمای کلی انجام نشد.<Button variant="subtle" onClick={() => overview.refetch()}>تلاش دوباره</Button></Alert>;
  const o = overview.data!;

  const open = (unpaid.data?.items ?? []).filter((e) => e.status === "Unpaid");
  const overdue = open.filter((e) => e.dueDate && e.dueDate.slice(0, 10) < range.to);
  const low = (products.data?.items ?? []).filter((p) => p.active && p.quantityOnHand <= p.lowStockThreshold);
  const follow = [
    open.length > 0 && { to: "/dashboard/finance/expenses", title: `${faNum(open.length)} هزینهٔ پرداخت‌نشده`, note: overdue.length ? `${faNum(overdue.length)} مورد سررسید گذشته` : "منتظر ثبت پرداخت" },
    low.length > 0 && { to: "/dashboard/finance/products", title: `${faNum(low.length)} محصول رو به اتمام`, note: low.slice(0, 3).map((p) => p.name).join("، ") },
    day.data?.needsReview && { to: "/dashboard/finance/reports?tab=close", title: "روز بسته‌شده نیاز به بررسی دارد", note: "پس از بستن روز، تراکنش درگاه رسیده است" },
    day.data && !day.data.closed && { to: "/dashboard/finance/reports?tab=close", title: `کنترل حساب ${faDate(range.to)}`, note: "صندوق و پوزها را بشمارید و روز را ببندید" },
  ].filter(Boolean) as { to: string; title: string; note: string }[];

  return (
    <>
      <div className={classes.kpis}>
        <div className={classes.kpi}><span>فروش خدمات و محصول</span><b>{rial(o.salesRial)}</b><small>پس از تخفیف و مرجوعی، در بازه</small></div>
        <div className={classes.kpi}><span>دریافت‌های ثبت‌شده</span><b>{rial(o.receiptsRial)}</b><small>شامل بیعانه و پوز تأییدشده؛ مترادف پول بانک نیست</small></div>
        <div className={classes.kpi}><span>پرداخت‌های مجموعه</span><b>{rial(o.expensesRial)}</b><small>هزینه‌های پرداخت‌شده در بازه</small></div>
        <div className={classes.kpi}><span>مطالبات از مشتری‌ها</span><b>{rial(o.receivableRial)}</b><small>مانده دریافت‌نشده تا پایان بازه</small></div>
      </div>

      <div className={classes.cols}>
        <section className={classes.panel}>
          <div className={classes.panelHead}><div><h2>پول مجموعه کجاست؟</h2><small>تا {faDate(range.to)} · مانده بر اساس ثبت‌ها</small></div></div>
          <div className={classes.list}>
            <div className={classes.row}><div><strong>صندوق نقدی</strong></div><span className={classes.amount}>{rial(o.cashRial)}</span></div>
            <div className={classes.row}><div><strong>بانک</strong><small>مانده بر اساس ثبت‌ها، نه موجودی زنده بانک</small></div><span className={classes.amount}>{rial(o.bankRial)}</span></div>
            <div className={classes.row}><div><strong>پوز و درگاه در انتظار واریز</strong><small>تا تسویه، قابل استفاده در بانک نیست</small></div><span className={classes.amount}>{rial(o.clearingRial)}</span></div>
            <div className={classes.row}><div><strong>جمع</strong></div><span className={classes.amount}>{rial(toNumber(o.cashRial) + toNumber(o.bankRial) + toNumber(o.clearingRial))}</span></div>
          </div>
        </section>

        <section className={classes.panel}>
          <div className={classes.panelHead}><div><h2>نیاز به پیگیری</h2><small>کارهایی که منتظر شماست</small></div></div>
          {follow.length ? (
            <div className={classes.list}>
              {follow.map((f) => (
                <Link key={f.title} to={f.to} className={classes.row}>
                  <div><strong>{f.title}</strong><small>{f.note}</small></div>
                  <IconChevronLeft size={18} color="#9a96a1" />
                </Link>
              ))}
            </div>
          ) : <div className={classes.empty}>فعلاً موردی برای پیگیری نیست.</div>}
        </section>
      </div>
    </>
  );
}
